import { defineStore } from 'pinia';

import moment from 'moment';

import localeCurrency from 'locale-currency';

import { useAppStore } from './app';

import * as ComponentUtilities from '../helpers/components';

import * as constants from '../constants';

import * as ThreeUtilities from '../components/Three/threeutilities';

import emitter from '../helpers/bus';

function getServiceUrl(service, options) {
    const config = new options.DriveCustomizer.Configuration();

    return `${config.endpoint}extension-acushnet-myjoys/${service}?apikey=${options.apiKey}`.replace('/v5', '/v2');
}

// Formats the price according to current locale settings.
function formatPrice(v, relative, locale, currency) {
    if (v == null || v === '' || v === 0) {
        return '';
    }

    const resolveCurrency = currency ?? localeCurrency.getCurrency(locale) ?? 'USD';

    const wholeFormatter = Intl.NumberFormat(locale ?? 'en-US', {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        notation: 'standard',
        currency: resolveCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const fractionFormatter = Intl.NumberFormat(locale ?? 'en-US', {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        notation: 'standard',
        currency: resolveCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const formatted = v % 1 === 0 ? wholeFormatter.format(v) : fractionFormatter.format(v);

    if (relative) {
        if (formatted.startsWith('-')) {
            return formatted;
        }

        return `+${formatted}`;
    }

    return formatted;
}

// eslint-disable-next-line import/prefer-default-export
export const useCustomizerStore = defineStore('customizer', {
    id: 'customizer',

    state: () => ({
        // Widget configuration options.
        options: {},

        // Currently selected controller.
        viewController: null,

        // Current vendor data.
        vendor: null,

        // Branding programs for the current vendor.
        brandingPrograms: null,

        // Personalization policy modal content.
        personalizationPolicy: null,

        // Size guide table.
        sizeGuide: null,

        // Size translations for the current vendor.
        sizeTranslations: null,

        // Used to modify the UI for locales with longer strings
        isWordyLocale: false,

        // Other translations according to the vendor locale.
        otherTranslations: null,

        // Yet more other translations according to the vendor locale.
        fallbackTranslations: null,

        // This message will be displayed in the PromoMessage component if present.
        specialMessage: null,

        // Complete product data provided by WebTop.
        product: null,

        // Initial instance saved by WebTop.
        initialInstance: null,

        // The very first controller that would allow to reset all selections to the very beginning.
        startingController: null,

        // A stack of captured controllers representing user selections.
        undoStack: [],

        cartErrors: 0,

        inErrorState: false,

        // Used to display an error message in the accordion when a selected size is backordered
        hasBackorderedError: false,

        // Used to disable the Add to Cart button when a selected size is backordered
        hasBackorderedSize: false,

        // The currently selected group
        currentGroup: null,

        // The camera view for the selected group
        currentView: null,

        // Used to display a modal that points to the login screen if the user isn't logged in
        addToWishlistMessage: null,

        // Did the last save instance operation resulted in errors?
        instanceError: false,

        // Is the product available?
        notAvailable: false,

        // Mirror personalization options.
        mirrorPersonalization: false,

        // Is this a vignette helper?
        vignette: false,
    }),

    getters: {
        /**
         * A list of groups visible in the UI.
         */
        visibleGroups() {
            // Safety measures, use empty list if there is no controller.
            if (this.viewController == null) {
                return [];
            }

            const groups = this.viewController.blueprint?.groups?.filter((group) => {
                // Skip groups that are hidden in the UI.
                if (group?.custom?.['fj-hidden'] === 'true') {
                    return false;
                }

                // Skip groups with no visible placements.
                const placements = this.visiblePlacements(group);

                if (placements.length === 0) {
                    return false;
                }

                return true;
            });

            return groups ?? [];
        },

        /**
         * Calculate a list of visible placements by group.
         */
        // eslint-disable-next-line no-unused-vars
        visiblePlacementsByGroup() {
            // Safety measures, use empty list if there is no controller.
            if (this.viewController == null) {
                return {};
            }

            const groups = {};

            this.visibleGroups.forEach((group) => {
                groups[group.code] = this.visiblePlacements(group);
            });

            return groups;
        },

        /**
         * The name of the shoe being customized.
         */
        productName() {
            if (this.viewController?.blueprint?.custom?.['fj-override-name']) {
                return this.viewController.blueprint.custom['fj-override-name'];
            }

            // Are there fallback translations?
            return this.useTranslations(`${this.productSku}_label`, this.viewController?.blueprint.name);
        },

        /**
         * The ID of the shoe being customized.
         */
        productSku() {
            return this.product?.sku;
        },

        /**
         * Extract product price from WebTop data.
         */
        productPrice() {
            if (this.product == null) {
                return '';
            }

            if (this.product.map == null) {
                return '';
            }

            const { currency, locale } = this.options;

            // For example,
            // currency: 'USD',
            // locale: 'en-US'

            let value = parseFloat(this.product.map);

            if (value === 0) {
                // Should we use actual?
                if (this.product.msrp != null && parseFloat(this.product.msrp) > 0) {
                    value = parseFloat(this.product.msrp);
                }
            }

            const price = formatPrice(value, false, locale, currency);

            return price;
        },

        /**
         * The gender/department of the shoe.
         */
        productDepartment() {
            return this.viewController?.blueprint?.custom?.['fj-gender'];
        },

        /**
         * Selections made so far.
         */
        selectionsMade() {
            const selections = [];

            this.visibleGroups.forEach((group) => {
                let totalPlacements = group.placements.length;
                let totalPlacementSelections = 0;

                if (this.isPersonalizationGroup(group)) {
                    totalPlacements = 1;

                    if (this.selectedPersonalizationType(group) === 'personalization-none') {
                        totalPlacementSelections = 1;
                    } else {
                        const selected = this.selectedPersonalization(group);

                        if (selected) {
                            totalPlacementSelections = selected.placeholder ? 0 : 1;
                        }
                    }
                } else {
                    group.placements.forEach((placement) => {
                        const selectedAtPlacement = this.viewController.selectedAtPlacement(placement.code);

                        if (selectedAtPlacement && !selectedAtPlacement.placeholder) {
                            totalPlacementSelections += 1;
                        }
                    });
                }

                if (totalPlacementSelections === totalPlacements) {
                    selections.push(group.name);
                }
            });

            return selections;
        },

        /**
         * Total selections made.
         */
        totalSelectionsMade() {
            return this.selectionsMade.length;
        },

        /**
         * Determines whether all required selections have been made.
         * Determines whether the Add to Cart button is enabled.
         */
        selectionsComplete() {
            return this.totalSelectionsMade === this.visibleGroups.length;
        },

        /**
         * Determines whether a selection has been made by the user.
         */
        userMadeSelection() {
            return this.undoStack.length > 0;
        },

        /**
         * Computed getter to translate things using translations provided by vendor data.
         */
        useTranslations() {
            return (key, defaultText) => {
                const withGallery = this.translateWithGallery(key);

                if (withGallery != null && withGallery.length > 0) {
                    return withGallery;
                }

                // No translations loaded yet.
                if (this.fallbackTranslations == null) {
                    return defaultText;
                }

                let useKey = key;

                // Override BOA colors.
                if (useKey === 'lacecolor' && this.viewController.blueprint.custom['fj-boa'] === 'boa') {
                    useKey = 'boacolor';
                }

                if (this.fallbackTranslations[useKey]) {
                    let useText = this.fallbackTranslations[useKey];

                    useText = useText.replace('Boa ', 'BOA ');

                    return useText;
                }

                return defaultText;
            };
        },

        /**
         * Checks if request originated from cart.
         */
        isFromCart() {
            const id = this.options?.lineItemId;

            if (id && id.length > 0) {
                return true;
            }

            return false;
        },

        /**
         * Gets a list of allowed logos.
         */
        allowedLogos() {
            const allowed = new Set();

            if (this.product?.logos) {
                this.product.logos.forEach((logoGroup) => {
                    if (logoGroup.logos) {
                        logoGroup.logos.forEach((logo) => {
                            allowed.add(logo.ecomValue);
                        });
                    }
                });
            }

            return allowed;
        },

        /**
         * Gets base site URL (which is hopefully the current site) using the add to cart link.
         */
        baseSiteUrl() {
            let serviceUrl = null;

            if (this.vendor?.cartUrl) {
                serviceUrl = this.vendor.cartUrl;
            }

            if (this.options.cartUrl) {
                serviceUrl = this.options.cartUrl;
            }

            if (serviceUrl) {
                const lastSlash = serviceUrl.lastIndexOf('/');

                if (lastSlash > 0) {
                    serviceUrl = serviceUrl.substr(0, lastSlash + 1);
                }
            }

            return serviceUrl;
        },

        lockedDesigns() {
            if (this.viewController == null) {
                return [];
            }

            const designs = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const group of this.viewController.blueprint.groups) {
                // eslint-disable-next-line no-restricted-syntax
                for (const placement of group.placements) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const component of placement.components) {
                        if (component.code.includes('LockedDesigns')) {
                            const { description } = component;

                            if (description.length > 0) {
                                let codes = description[0].description;

                                if (codes) {
                                    codes = codes.split(/[\n,.;\s]/g).map((x) => x.trim()).filter((x) => x.length > 0);

                                    designs.push(...codes);
                                }
                            }
                        }
                    }
                }
            }

            return designs;
        },

        hiddenLogos() {
            if (this.viewController == null) {
                return [];
            }

            const designs = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const group of this.viewController.blueprint.groups) {
                // eslint-disable-next-line no-restricted-syntax
                for (const placement of group.placements) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const component of placement.components) {
                        if (component.code.includes('HiddenLogos')) {
                            const { description } = component;

                            if (description.length > 0) {
                                let codes = description[0].description;

                                if (codes) {
                                    codes = codes.split(/[\n,.;\s]/g).map((x) => x.trim()).filter((x) => x.length > 0);

                                    designs.push(...codes);
                                }
                            }
                        }
                    }
                }
            }

            return designs;
        },

        hideBoa() {
            if (this.options.hideBoa) {
                return true;
            }

            return false;
        },

        disableEdit() {
            if (this.options.disableEdit) {
                return true;
            }

            if (this.options.sourceInstance && this.lockedDesigns.includes(this.options.sourceInstance)) {
                return true;
            }

            return false;
        },
    },

    actions: {
        /**
         * Save options.
         */
        setOptions(options) {
            this.options = options;

            // Replace empty values that we are getting from SFCC with proper nulls.
            if (this.options.products === '') {
                this.options.products = null;
            }

            if (this.options.package === '') {
                this.options.package = null;
            }
        },

        /**
         * Start loading products.
         */
        load(options) {
            if (options) {
                Object.assign(this.options, options);
            }

            this.getWebTopData().then(() => {
                // Continue loading the controller.
                this.loadCustomizeFlow();
            }).catch(() => {
                this.notAvailable = true;
            });
        },

        // Load data from Web
        getWebTopData(controller) {
            let products = this.options.products;
            if (Array.isArray(products)) {
                products = products[0];
            }

            // Current product may have changed due to customer selections.
            if (this.viewController?.blueprint?.styleCode) {
                products = this.viewController.blueprint.styleCode;
            }

            // Overridden controller when loading controllers from rules.
            if (controller) {
                if (controller?.blueprint?.styleCode) {
                    products = controller.blueprint.styleCode;
                }
            }

            products = products?.toString();

            let vignette = false;

            if (products && products.endsWith('-Vignette')) {
                vignette = true;
                products = products.replace('-Vignette', '');
            }

            this.vignette = vignette;

            return new Promise((resolve, reject) => {
                const webTopParameters = JSON.stringify({
                    vendor: this.options.vendor,
                    environment: this.options.environment,
                    product: this.options.instance ? '' : products,
                    instance: this.options.instance,
                    package: this.options.package,
                });

                // Don't reload the data if we already have it.
                if (this.webTopDataLoaded === webTopParameters) {
                    resolve();
                    return;
                }

                // Resolve MyJoys specific data first.
                fetch(getServiceUrl('vendor-data', this.options), {
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: webTopParameters,
                })
                    .then((response) => response.json())
                    .then((vendorData) => {
                        // Is this product available in the region?
                        if (vendorData?.product?.sku == null) {
                            // Nope.
                            reject();
                        } else {
                            this.options.sourceInstance = this.options.instance;

                            // We no longer need to hold on to the instance ID since it would be overriding future data requests.
                            delete this.options.instance;

                            // Save request.
                            this.webTopDataLoaded = JSON.stringify({
                                vendor: this.options.vendor,
                                environment: this.options.environment,
                                // Use data from the loaded product SKU.
                                product: vendorData.mappedProduct?.toString(),
                                instance: this.options.instance,
                                package: this.options.package,
                            });

                            this.vendor = vendorData.vendor;
                            this.brandingPrograms = vendorData.brandingPrograms;
                            this.personalizationPolicy = vendorData.personalizationPolicy;
                            this.sizeGuide = vendorData.sizeGuide;
                            this.sizeTranslations = vendorData.sizeTranslations;
                            this.otherTranslations = vendorData.otherTranslations;
                            this.fallbackTranslations = vendorData.fallbackTranslations;
                            this.specialMessage = vendorData.specialMessage;
                            this.product = vendorData.product;
                            this.productInventory = vendorData.productInventory;
                            this.initialInstance = vendorData.instance;

                            this.componentLookup = null;

                            // Update the locale based on the selected vendor.
                            if (vendorData.vendor?.sales_office_default_locale) {
                                this.options.locale = vendorData.vendor?.sales_office_default_locale;

                                this.isWordyLocale = this.options.locale.includes('de') || this.options.locale.includes('fr')
                                || this.options.locale.includes('ko');
                            }

                            // Update the product based on the mapping.
                            if (vendorData.mappedProduct) {
                                if (vignette) {
                                    this.options.products = [`${vendorData.mappedProduct}-Vignette`];
                                } else {
                                    this.options.products = [vendorData.mappedProduct];
                                }
                            }

                            // Update the site list.
                            const sites = [`Vendor-${this.options.vendor}`];
                            if (this.options.package) {
                                sites.push(`Package-${this.options.package}`);
                            }

                            this.options.site = sites.join(',');

                            resolve();
                        }
                    });
            });
        },

        /**
         * Calculate default controller options.
         */
        defaultControllerOptions() {
            const noCaching = this.options.disableCaching === 'true' || this.options.disableCaching === true;

            const options = {
                apiKey: this.options.apiKey,
                products: this.options.products,
                preview: this.options.preview,
                site: this.options.site,
                version: this.options.version,
                locale: this.options.locale,
                disableCaching: noCaching,
            };

            if (this.options.endpoint) {
                options.endpoint = this.options.endpoint;
            }

            return options;
        },

        /**
         * Is the size and width available in the product record?
         */
        sizeWidthAvailable(product, size, width) {
            let available = {
                status: true,
            };

            if (product.sizes) {
                const sizeRecord = product.sizes.find((s) => s.ecomValue === size);
                if (sizeRecord == null) {
                    available = {
                        status: false,
                    };
                } else if (sizeRecord.widths) {
                    const widthRecord = sizeRecord.widths.find((w) => w.ecomValue.trim() === width);
                    if (widthRecord == null) {
                        available = {
                            status: false,
                        };
                    } else if (!widthRecord.available) {
                        // Is it a parseable date?
                        // 4/20/2023 12:00:00 AM

                        const date = moment(widthRecord.availableDate, 'M/DD/YYYY HH:mm:ss A');

                        if (date.isValid()) {
                            available = {
                                status: true,
                                backorder: date.toDate(),
                            };
                        } else {
                            available = {
                                status: false,
                            };
                        }
                    }
                }
            }

            return available;
        },

        /**
         * Loads and availability for components mentioned in the blueprint.
         */
        initializeAvailability({ controller, blueprint }) {
            return new Promise((resolve) => {
                // Reload WebTop data since pricing and availability may have changed if another product is selected.
                this.getWebTopData(controller).then(() => {
                    this.componentLookup = {};
                    this.backorderLookup = {};

                    blueprint.groups.forEach((group) => {
                        group.placements.forEach((placement) => {
                            // Merge availability on the fly to make mixed environments work (i.e. product data from product + availability from vendortest).

                            if (placement.code.includes('SizeWidth')) {
                                placement.components.forEach((component) => {
                                    if (component.placeholder) {
                                        return;
                                    }

                                    const size = component.custom?.['fj-size-value'];
                                    const width = component.custom?.['fj-width-value'];

                                    let available;

                                    if (this.productInventory) {
                                        available = this.sizeWidthAvailable(this.productInventory, size, width);
                                    } else {
                                        available = this.sizeWidthAvailable(this.product, size, width);
                                    }

                                    if (available.backorder) {
                                        this.backorderLookup[component.code] = available.backorder;
                                    }

                                    if (!available || !available.status) {
                                        if (!this.options.alwaysAvailable) {
                                            this.componentLookup[component.code] = false;
                                        }
                                    }
                                });
                            }

                            const parameterName = placement.custom?.['fj-parameter-name'];
                            if (parameterName) {
                                // Is it a style group? Merge availability.
                                if (this.product?.styleGroups != null) {
                                    const styleGroup = this.product.styleGroups.find((g) => g.parameterName === parameterName);

                                    if (styleGroup?.options != null) {
                                        placement.components.forEach((component) => {
                                            if (component.placeholder) {
                                                return;
                                            }

                                            const ecommValue = component.custom['fj-parameter-value'];

                                            const option = styleGroup.options.find((o) => o.ecomValue === ecommValue);

                                            // If not found, mark component as unavailable.
                                            if (option == null) {
                                                if (!this.options.alwaysAvailable) {
                                                    this.componentLookup[component.code] = false;
                                                }
                                            }
                                        });
                                    }
                                }
                            }
                        });
                    });

                    resolve();
                });
            });
        },

        /**
         * Retrieve a component price.
         */
        // eslint-disable-next-line no-unused-vars
        getPrice({ component }) {
            return new Promise((resolve) => {
                resolve(10);
            });
        },

        /**
         * Attaches events to the controller.
         */
        attachControllerEvents(controller) {
            controller.on('setReactive', (v) => {
                // Vue 3 doesn't need Vue.set anymore.
                v.target[v.key] = v.value;
                return true;
            });

            controller.on('initializeAvailability', (o) => this.initializeAvailability({
                controller: o.controller,
                blueprint: o.blueprint,
            }));

            controller.on('beforeTransferCompatibleState', (o) => {
                this.initializeFromAnotherState(o.controller, o.from);
            });

            controller.on('beforeReloadBlueprint', () => {
                const appStore = useAppStore();

                appStore.setIsLoading(true);

                emitter.$emit('capturePoster', {
                    duration: 15000,
                    preventRelease: true,
                });
            });

            controller.on('isAvailable', (component) => this.isAvailable(component));

            controller.on('getPrice', (component) => this.getPrice({
                component,
                alwaysAvailable: this.options.alwaysAvailable,
                testPrices: this.options.testPrices,
                ignoreOnline: this.options.ignoreOnline,
                products: this.products,
            }));

            controller.on('stateChanged', () => {
                this.validateBackordered(controller);
            });
        },

        /**
         * Load controller.
         */
        loadController(controller) {
            this.attachControllerEvents(controller);

            controller.load();
        },

        /**
         * Check if the component is available according to the current product configuration.
         */
        isAvailable(component) {
            if (this.componentLookup && this.componentLookup[component.code] === false) {
                return false;
            }

            if (!component.available) {
                return false;
            }

            return true;
        },

        /**
         * Since we are using product-specific option IDs, some placement selections cannot be copied cleanly to the new state,
         * so we need to resolve them manually.
         */
        initializeFromAnotherState(controller, from) {
            controller.state.forEach((s) => {
                const placementCode = s.placement.code;

                // Find matching placement.
                const anotherPlacement = from.state.find((x) => x.placement.code === placementCode);

                if (anotherPlacement) {
                    const selectionCode = anotherPlacement.component.custom?.['fj-parameter-value'];

                    if (selectionCode) {
                        // Is there a component with matching code in the new placement?
                        const newSelection = s.placement.components.find((x) => x.custom?.['fj-parameter-value'] === selectionCode);

                        if (newSelection) {
                            controller.updateComponent(placementCode, newSelection.code);
                        }
                    }
                }
            });
        },

        /**
         * Load customization flow.
         */
        loadCustomizeFlow() {
            const controller = new this.options.DriveCustomizer.Controller({
                ...this.defaultControllerOptions(),
            });

            controller.traceUpdated = true;
            controller.traceAllowed = true;
            controller.traceAvailable = true;

            controller.on('afterInitializeBlueprint', () => {
                // Load actual product options.
                this.loadColors({
                    controllers: [controller],
                });
            });

            // Load controller.
            this.loadController(controller);
        },

        /**
         * Update translations for the current locale from the controller.
         */
        updateTranslations(controller) {
            if (controller.blueprint?.translations) {
                const appStore = useAppStore();

                const locale = this.options.locale || 'en';

                let messages = appStore.translations.getLocaleMessage(locale);

                if (messages == null) {
                    messages = {};
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const k of Object.keys(controller.blueprint.translations)) {
                    messages[k] = controller.blueprint.translations[k];
                }

                appStore.translations.setLocaleMessage(locale, messages);
            }
        },

        /**
         * Continue initializing the controller and configure product colors.
         */
        loadColors({ controllers }) {
            const appStore = useAppStore();

            let showController = null;

            if (controllers.length > 0) {
                // eslint-disable-next-line
                showController = controllers[0];
            }

            this.updateTranslations(showController);

            if (showController) {
                if (this.startingController == null) {
                    // First time initialization, restore initial selections from WebTop and options.
                    this.setInitialSelections(showController);

                    // Make a copy of the first complete controller.
                    // `loadColors` may be called several times, for example when changing the traction system, so only capture the first controller.
                    this.startingController = showController.clone();
                }

                this.showController(showController);

                // Figure out the starting step.
                if (showController?.blueprint?.custom?.['ps-select-phone-first']) {
                    appStore.setCurrentStep(constants.STEP_SELECT_PHONE);
                } else {
                    appStore.setCurrentStep(constants.STEP_SELECT_BUNDLE);
                }

                // Check if there are any assemblies that are already loaded, and if not, select something.
                this.loadDefaultAssemblies();

                // Populate initial translations and WebTop overrides.
                this.populateGalleryTranslations();
            }
        },

        /**
         * Sets initial selections.
         */
        setInitialSelections(controller) {
            this.setInitialSizeWidth(controller);
            this.setInitialColors(controller);
        },

        /**
         * Sets initial colors from WebTop instance.
         */
        setInitialColors(controller) {
            if (this.initialInstance) {
                controller.state.forEach((s) => {
                    if (s.placement.custom?.['fj-parameter-name'] != null) {
                        const parameter = `${s.placement.custom?.['fj-parameter-name'].replace('$', '')}color`;

                        const selectValue = this.initialInstance[parameter];
                        if (selectValue != null) {
                            // Find a component with the same parameter value.
                            const selectComponent = s.placement.components.find((c) => {
                                if (!controller.isAvailable(c)) {
                                    return false;
                                }

                                const value = c.custom?.['fj-parameter-value'];
                                if (value !== selectValue) {
                                    return false;
                                }

                                return true;
                            });

                            if (selectComponent) {
                                controller.updateComponent(s.placement.code, selectComponent.code);
                            }
                        }
                    }

                    if (this.isPersonalizationPlacement(s.placement)) {
                        this.clearPlacement(controller, s.placement.code);
                    }

                    if (this.initialInstance.logo_right) {
                        this.setInitialLogo(controller, this.initialInstance.logo_right, 'MONOGRAMMINGLOGO11');
                    }

                    if (this.initialInstance.logo_left) {
                        this.setInitialLogo(controller, this.initialInstance.logo_left, 'MONOGRAMMINGLOGO21');
                    }

                    // TODO: We technically should handle vendor logos. Due Edit from cart would happen mostly on DTC?

                    if (this.initialInstance.script_type && this.initialInstance.threadcolor && this.initialInstance.personalization_text_right) {
                        this.setInitialText(
                            controller,
                            this.initialInstance.script_type,
                            this.initialInstance.threadcolor,
                            this.initialInstance.personalization_text_right,
                            'MONOGRAMMINGTEXT11',
                        );
                    }

                    if (this.initialInstance.script_type && this.initialInstance.threadcolor && this.initialInstance.personalization_text_left) {
                        this.setInitialText(
                            controller,
                            this.initialInstance.script_type,
                            this.initialInstance.threadcolor,
                            this.initialInstance.personalization_text_left,
                            'MONOGRAMMINGTEXT21',
                        );
                    }
                });
            }
        },

        /**
         * Sets initial text.
         */
        setInitialText(controller, script, thread, text, placementCode) {
            const placement = controller.findPlacement(placementCode);

            const component = placement.components.find((c) => c.custom?.['fj-parameter-value'] === thread && c.custom?.['fj-script-value'] === script);

            if (component) {
                controller.updateComponent(placementCode, component.code);

                controller.updatePersonalization(placementCode, text);
            }
        },

        /**
         * Set initial logo.
         */
        setInitialLogo(controller, ecomValue, placementCode) {
            const placement = controller.findPlacement(placementCode);

            const component = placement.components.find((c) => c.custom?.['fj-parameter-value'] === ecomValue);

            if (component) {
                controller.updateComponent(placementCode, component.code);
            }
        },

        /**
         * Sets initial size and width for both shoes.
         */
        setInitialSizeWidth(controller) {
            let sizeLeft = null;
            let sizeRight = null;
            let widthLeft = null;
            let widthRight = null;

            if (this.initialInstance?.size_left != null) {
                sizeLeft = this.initialInstance.size_left;
            }

            // Override from options.
            if (this.options.sizeLeft != null && this.options.sizeLeft.length > 0) {
                sizeLeft = this.options.sizeLeft;
            }

            if (this.initialInstance?.size_right != null) {
                sizeRight = this.initialInstance.size_right;
            }

            // Override from options.
            if (this.options.sizeRight != null && this.options.sizeRight.length > 0) {
                sizeRight = this.options.sizeRight;
            }

            // If the right is still empty, default it to the left one.
            if (sizeRight == null && sizeLeft != null) {
                sizeRight = sizeLeft;
            }

            if (this.initialInstance?.width_left != null) {
                widthLeft = this.initialInstance.width_left;
            }

            // Override from options.
            if (this.options.widthLeft != null && this.options.widthLeft.length > 0) {
                widthLeft = this.options.widthLeft;
            }

            if (this.initialInstance?.width_right != null) {
                widthRight = this.initialInstance.width_right;
            }

            // Override from options.
            if (this.options.widthRight != null && this.options.widthRight.length > 0) {
                widthRight = this.options.widthRight;
            }

            // If the right is still empty, default it to the left one.
            if (widthRight == null && widthLeft != null) {
                widthRight = widthLeft;
            }

            // If we have the left size, select it.
            if (sizeLeft != null) {
                controller.updateSimilarComponent('LeftSizeWidth', 'Size', `Size-${sizeLeft.trim()}`);
            }

            if (widthLeft != null) {
                controller.updateSimilarComponent('LeftSizeWidth', 'Width', `Width-${widthLeft.trim()}`);
            }

            // If we have the right size, select it.
            if (sizeRight != null) {
                controller.updateSimilarComponent('RightSizeWidth', 'Size', `Size-${sizeRight.trim()}`);
            }

            if (widthRight != null) {
                controller.updateSimilarComponent('RightSizeWidth', 'Width', `Width-${widthRight.trim()}`);
            }

            // Validate initial backorder state.
            this.validateBackordered(controller);
        },

        /**
         * Validate backordered state.
         */
        validateBackordered(controller) {
            const useController = controller ?? this.viewController;

            const selectedLeft = useController.selectedAtPlacement('LeftSizeWidth');

            let anyOut = false;

            if (selectedLeft && !selectedLeft?.placeholder) {
                const backorderDate = this.isBackordered(selectedLeft);
                if (backorderDate) {
                    anyOut = true;
                    this.setHasBackorderedSize(true);
                }
            }

            const selectedRight = useController.selectedAtPlacement('RightSizeWidth');
            if (selectedRight && !selectedRight?.placeholder) {
                const backorderDate = this.isBackordered(selectedRight);
                if (backorderDate) {
                    anyOut = true;
                    this.setHasBackorderedSize(true);
                }
            }

            if (!anyOut) {
                this.setHasBackorderedSize(false);
                this.setHasBackorderedError('');
            }
        },

        /**
         * Loads defaults assemblies for the current controller.
         */
        loadDefaultAssemblies() {
        },

        /**
         * Reloads controller with a different set of products.
         */
        reloadController(product) {
            return new Promise((resolve) => {
                const reloaded = new this.options.DriveCustomizer.Controller({
                    ...this.defaultControllerOptions(),
                    ...{
                        products: [product],
                    },
                });

                reloaded.on('afterInitializeState', () => {
                });

                reloaded.on('afterInitializeBlueprint', () => {
                    resolve(reloaded);
                });

                this.loadController(reloaded);
            });
        },

        /**
         * Selects a controller to show in the UI.
         */
        showController(controller) {
            this.viewController = controller;

            let pickView = null;
            if (this.currentView) {
                // Preserve the selected view.
                pickView = this.viewController?.blueprint?.views?.find((v) => v.code === this.currentView.code);
            }

            if (pickView == null) {
                pickView = this.viewController?.blueprint?.views?.[0];
            }

            this.setCurrentView(pickView);
        },

        /**
         * Reloads controllers with the new bundle while attempting to save the current printable configuration.
         */
        selectBundle(product) {
            this.reloadController(product).then((controller) => {
                this.showController(controller);
            });
        },

        /**
         * Puts the app in an error state if the user tries to add to cart before all selections have been made.
         * Using a count so the accordions will collapse when the Add to Cart button is clicked even when in the error state.
         */
        setCartErrors(numErrors) {
            this.cartErrors += numErrors;
        },

        /**
         * Puts the app in an error state if the user tries to add to cart before all selections have been made.
         */
        setErrorState(state) {
            this.inErrorState = state;
        },

        /**
         * Sets whether an error message should be displayed when a disabled add to cart button is clicked.
         */
        setHasBackorderedError() {
            this.hasBackorderedError = this.hasBackorderedSize;
        },

        /**
         * Sets whether a selected shoe size is backordered.
         */
        setHasBackorderedSize(hasBackorderedSize) {
            this.hasBackorderedSize = hasBackorderedSize;
        },

        /**
         * Used to show user feedback after attempting to add to their wishlist.
         */
        setAddToWishlistMessage(message) {
            this.addToWishlistMessage = message;
        },

        /**
         * Resets customizations.
         */
        resetCustomization() {
            // Reset to the very first loaded controller.
            const newBeginning = this.startingController.clone();

            // Reset events since the old handles might have closures capturing old data.
            newBeginning.off();
            this.attachControllerEvents(newBeginning);

            this.showController(newBeginning);

            this.undoStack = [];

            this.cartErrors = 0;
            this.inErrorState = false;
            this.hasBackorderedSize = false;
            this.hasBackorderedError = false;

            this.currentView = this.viewController?.blueprint?.views?.[0];
        },

        /**
         * Save undo checkpoint.
         */
        undoCheckpoint() {
            // Push the current state into the undo stack so it could be restored later.
            this.undoStack.push(this.viewController.clone());
        },

        /**
         * Stores the previous selection to be used for Undo functionality.
         * Updates the selected component.
         */
        setSelection(placementCode, optionValue) {
            this.undoCheckpoint();

            // Update the current controller.
            this.viewController.updateComponent(placementCode, optionValue);

            // Get out of error state if all selections have been made.
            if (this.selectionsComplete && this.inErrorState) {
                this.inErrorState = false;
            }
        },

        /**
         * Sets the current current group.
         */
        setCurrentGroup(groupName) {
            this.currentGroup = groupName;
        },

        /**
         * Sets the current camera view.
         */
        setCurrentView(view) {
            this.currentView = view;
        },

        /**
         * Sets the current camera view when a group is selected.
         */
        setCurrentViewByGroup(group) {
            const checkView = group.custom?.['fj-prefer-try-view'];

            if (checkView) {
                // Try to highlight this group.

                const materialName = `${checkView}_`;

                // emitter.$emit('highlightMeshes', {
                //     controller: this.viewController,
                //     meshes: (mesh) => {
                //         if (mesh?.material?.name.includes(checkView)) {
                //             return true;
                //         }

                //         return false;
                //     },
                //     color: ThreeUtilities.toColor3('#000000'),
                //     delay: 100,
                //     duration: 1200,
                //     transition: 500,
                // });

                emitter.$emit('clearOverlays');
                emitter.$emit('overlayMeshes', {
                    controller: this.viewController,
                    meshes: (mesh) => {
                        if (mesh?.material?.name.includes(materialName)) {
                            return true;
                        }

                        return false;
                    },
                    color: ThreeUtilities.toColor3(this.viewController.blueprint.custom['fj-highlight-color'] ?? '#000000'),
                    alpha: +(this.viewController.blueprint.custom['fj-highlight-alpha'] ?? 0.6),
                    delay: 100,
                    duration: 1000,
                    transition: 250,
                });

                const tryView = this.viewController.blueprint.views.find((view) => view.code === checkView);
                if (tryView?.custom?.['three-camera-alpha'] != null) {
                    this.currentView = tryView;

                    return;
                }
            }

            const selectedView = this.viewController.blueprint.views.find((view) => view.code === group.custom['fj-prefer-view']
                || view.name === group.custom['fj-prefer-view']);

            if (selectedView) {
                this.currentView = selectedView;
            }
        },

        /**
         * Return to the last made selection.
         */
        undoSelections() {
            if (this.undoStack.length > 0) {
                // Take the last captured controller and make it active.
                const lastState = this.undoStack.pop();

                this.showController(lastState);
            }
        },

        /**
         * Helper method, get a list of visible placements.
         */
        visiblePlacements(group) {
            if (group.placements == null) {
                return null;
            }

            return group.placements.filter((placement) => {
                if (placement.hidden) {
                    return false;
                }

                // Skip hidden placements.
                if (placement?.custom?.['fj-hidden'] === 'true') {
                    return false;
                }

                // Skip placements with only one valid choice.
                if (placement?.components?.length <= 1) {
                    return false;
                }

                return true;
            });
        },

        /**
         * Saves current controller configuration as instance in WebTop.
         */
        saveInstance(options) {
            return new Promise((resolve, reject) => {
                this.saveWebTopInstance(options).then((instance) => {
                    if (options && options.instanceOnly) {
                        resolve(instance);
                    } else if (!instance || instance.instance <= 0) {
                        this.setInstanceError(true);

                        reject(new Error('Save instance error'));
                    } else {
                        this.generateSnapshots().then((images) => {
                            if (this.vignette) {
                                resolve({});
                                return;
                            }

                            this.saveRecipe(instance, images).then((recipe) => {
                                resolve(recipe);
                            });
                        });
                    }
                });
            });
        },

        /**
         * Save customizer recipe.
         */
        saveRecipe(instance, images) {
            return new Promise((resolve) => {
                this.viewController.saveRecipe({
                    translateRecipeAfter: (recipe) => {
                        // Save instance data.
                        if (instance.instance !== '-1' && instance.instance !== -1) {
                            instance.webTopConfiguration.custom_instance_id = instance.instance.toString();
                        }

                        recipe.custom['fj-configuration'] = JSON.stringify(instance.webTopConfiguration);
                        recipe.custom['fj-instance'] = instance.instance;

                        // Save images.
                        images.forEach((image) => {
                            const view = recipe.views.find((v) => v.code === image.view && this.viewController.id === image.controller);

                            if (view) {
                                view.clientRender = image.data;
                            }
                        });
                    },
                }).then((recipe) => {
                    // Fetch the actual recipe.
                    fetch(recipe.location).then((response) => response.json()).then((data) => {
                        resolve(data);
                    });
                });
            });
        },

        /**
         * Creates a client side render of snapshots.
         */
        generateSnapshots() {
            return new Promise((resolve) => {
                const snapshots = [];

                // Create local snapshots.
                const controllers = [this.viewController];

                controllers.forEach((controller) => {
                    controller.blueprint.views.forEach((view) => {
                        if (view.bakeInRecipe && view.viewType === 'Camera') {
                            snapshots.push({
                                controller: controller.id,
                                view,
                            });
                        }
                    });
                });

                const images = [];

                this.prerender(snapshots, images, resolve);
            });
        },

        /**
         * Prerender a single view.
         */
        prerender(snapshots, images, resolve) {
            if (snapshots.length > 0) {
                const view = snapshots.shift();

                if (this.viewController.blueprint.custom['use-snapshot-service'] === 'true') {
                    images.push({
                        controller: view.controller,
                        view: view.view.code,
                        data: 'snapshot:',
                    });

                    this.prerender(snapshots, images, resolve);
                } else {
                    emitter.$emit('snapshot', {
                        controllerId: view.controller,
                        view: view.view.code,
                        done: (data) => {
                            images.push({
                                controller: view.controller,
                                view: view.view.code,
                                data,
                            });

                            this.prerender(snapshots, images, resolve);
                        },
                    });
                }
            } else {
                resolve(images);
            }
        },

        /**
         * Saves current controller configuration as instance in WebTop.
         */
        saveWebTopInstance(options) {
            return new Promise((resolve) => {
                // From what I can tell from existing services, these are hardcoded rather than generated on the fly based on the product configuration.
                // .... saddlecolor:n("saddle"),accentcolor:n("accent"),accent2color:n("accent2"),lacecolor:n("lace")}},t.ge ....

                const parameters = {};

                // Baseline parameters.

                parameters.vendorKey = this.options.vendor ?? '';
                parameters.sales_office_id = this.vendor.sales_office_id;
                parameters.branding_package_id = this.options.package ?? -1;
                parameters.custom_instance_id = 0;
                parameters.quantity = 1;

                // Selected product.
                parameters.sku = this.product.sku;

                // Selected size.
                const leftSize = this.viewController.selectedAtPlacement('LeftSizeWidth');
                if (leftSize?.custom?.['fj-size-value']) {
                    parameters.size_left = leftSize?.custom?.['fj-size-value'];
                } else {
                    parameters.size_left = '';
                }
                if (leftSize?.custom?.['fj-width-value']) {
                    parameters.width_left = leftSize?.custom?.['fj-width-value'];
                } else {
                    parameters.width_left = '';
                }

                const rightSize = this.viewController.selectedAtPlacement('RightSizeWidth');
                if (rightSize?.custom?.['fj-size-value']) {
                    parameters.size_right = rightSize?.custom?.['fj-size-value'];
                } else {
                    parameters.size_right = '';
                }
                if (rightSize?.custom?.['fj-width-value']) {
                    parameters.width_right = rightSize?.custom?.['fj-width-value'];
                } else {
                    parameters.width_right = '';
                }

                // In some cases the size selection is required.
                if (options && options.forceSize) {
                    if (parameters.size_left === '' || parameters.width_left === '') {
                        const firstSize = this.viewController.findPlacement('LeftSizeWidth').components.find((x) => !x.placeholder
                            && this.viewController.isAvailable(x));

                        if (firstSize) {
                            parameters.size_left = firstSize?.custom?.['fj-size-value'];
                            parameters.width_left = firstSize?.custom?.['fj-width-value'];
                        }
                    }

                    if (parameters.size_right === '' || parameters.width_right === '') {
                        const firstSize = this.viewController.findPlacement('RightSizeWidth').components.find((x) => !x.placeholder
                            && this.viewController.isAvailable(x));

                        if (firstSize) {
                            parameters.size_right = firstSize?.custom?.['fj-size-value'];
                            parameters.width_right = firstSize?.custom?.['fj-width-value'];
                        }
                    }
                }

                // Personalization parameters.
                parameters.logo_left = '';
                parameters.logo_right = '';

                parameters.personalization_text_left = '';
                parameters.personalization_text_right = '';

                parameters.script_type = '';

                parameters.threadcolor = '';

                // Add personalization.
                const left = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationLeft');
                const right = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationRight');

                if (left && right) {
                    const leftType = this.selectedPersonalizationType(left);
                    const rightType = this.selectedPersonalizationType(right);

                    const leftSelected = this.selectedPersonalization(left);
                    const rightSelected = this.selectedPersonalization(right);

                    if (leftType === 'personalization-logo') {
                        if (leftSelected?.custom?.['fj-parameter-value']) {
                            parameters.logo_left = leftSelected.custom['fj-parameter-value'];
                        }
                    }

                    if (rightType === 'personalization-logo') {
                        if (rightSelected?.custom?.['fj-parameter-value']) {
                            parameters.logo_right = rightSelected.custom['fj-parameter-value'];
                        }
                    }

                    if (leftType === 'personalization-monogram') {
                        if (leftSelected?.custom?.['fj-script-value']) {
                            parameters.script_type = leftSelected.custom['fj-script-value'];
                        }

                        if (leftSelected?.custom?.['fj-parameter-value']) {
                            parameters.threadcolor = leftSelected.custom['fj-parameter-value'];
                        }

                        parameters.personalization_text_left = leftSelected.custom['personalization-text'];
                    }

                    if (rightType === 'personalization-monogram') {
                        if (rightSelected?.custom?.['fj-script-value']) {
                            parameters.script_type = rightSelected.custom['fj-script-value'];
                        }

                        if (rightSelected?.custom?.['fj-parameter-value']) {
                            parameters.threadcolor = rightSelected.custom['fj-parameter-value'];
                        }

                        parameters.personalization_text_right = rightSelected.custom['personalization-text'];
                    }
                }

                // Hrm. WebTop really wants these values to be filled in.
                // Should we pull them from the default config?
                parameters.accent4color = '';
                parameters.accent3color = '';
                parameters.accent2color = '';
                parameters.accentcolor = '';
                parameters.basecolor = '';
                parameters.lacecolor = '';
                parameters.saddlecolor = '';

                if (this.product?.defaultInspiration) {
                    const hasColor = (product, group, color) => {
                        const styleGroup = product.styleGroups.find((x) => x.parameterName === group);

                        if (styleGroup?.options) {
                            const option = styleGroup.options.find((x) => x.ecomValue === color);

                            if (option && option.active && option.available) {
                                return true;
                            }
                        }

                        return false;
                    };

                    [
                        ['accentcolor', '$accent'],
                        ['accent2color', '$accent2'],
                        ['accent3color', '$accent3'],
                        ['basecolor', '$base'],
                        ['lacecolor', '$lace'],
                        ['saddlecolor', '$saddle'],
                    ].forEach(([v, group]) => {
                        // Don't set default values if the style group is not attached to the product.
                        const styleGroup = this.product.styleGroups.find((x) => x.parameterName === group);
                        if (styleGroup == null) {
                            return;
                        }

                        if (this.product.defaultInspiration[v] && hasColor(this.product, group, this.product.defaultInspiration[v])) {
                            parameters[v] = this.product.defaultInspiration[v];
                        }

                        if (parameters[v] === '') {
                            if (hasColor(this.product, group, '827')) {
                                parameters[v] = '827';
                            }
                        }

                        if (parameters[v] === '') {
                            if (hasColor(this.product, group, '468')) {
                                parameters[v] = '468';
                            }
                        }

                        if (parameters[v] === '') {
                            if (hasColor(this.product, group, '437')) {
                                parameters[v] = '437';
                            }
                        }

                        if (parameters[v] === '') {
                            if (styleGroup?.options && styleGroup.options.length > 0) {
                                const groupOptions = [...styleGroup.options].filter((x) => x.active && x.available);

                                groupOptions.sort((a, b) => a.id - b.id);

                                parameters[v] = groupOptions[0].ecomValue;
                            }
                        }
                    });
                }

                // Components.
                this.viewController.state.forEach((s) => {
                    if (s.placement.custom?.['fj-parameter-name'] != null) {
                        const parameter = `${s.placement.custom?.['fj-parameter-name'].replace('$', '')}color`;

                        if (s.component.custom?.['fj-parameter-value'] != null) {
                            parameters[parameter] = s.component.custom?.['fj-parameter-value'];
                        }
                    }
                });

                // Save WebTop instance.
                fetch(getServiceUrl('save-instance', this.options), {
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        vendor: this.options.vendor,
                        environment: this.options.environment,
                        parameters,
                    }),
                })
                    .then((response) => response.json())
                    .then((instanceData) => {
                        resolve({
                            webTopConfiguration: parameters,
                            instance: instanceData,
                        });
                    });
            });
        },

        /**
         * Recreate WebTop configuration object from a recipe, but also add stuff such as image data.
         */
        formatDataFromRecipe(recipe) {
            const configuration = JSON.parse(recipe.custom['fj-configuration']);

            // Add images.
            let imageIndex = 0;

            const thumbnails = [];

            recipe.views.forEach((view) => {
                if (view.previewJpg) {
                    const imageId = `myJoysImageUrl${imageIndex}`;

                    let url = view.previewJpg;

                    if (imageIndex === 0) {
                        url = view.previewPng;
                    }

                    if (url.startsWith('//')) {
                        url = `https:${url}`;
                    }

                    configuration[imageId] = url;

                    imageIndex += 1;

                    if (view.variants) {
                        const thumb = view.variants.find((x) => x.code === 'thumbnail');

                        if (thumb) {
                            thumbnails.push(thumb.preview);
                        }
                    }
                }
            });

            if (thumbnails.length > 0) {
                thumbnails.forEach((thumb) => {
                    const imageId = `myJoysImageUrl${imageIndex}`;

                    let url = thumb;

                    if (url.startsWith('//')) {
                        url = `https:${url}`;
                    }

                    configuration[imageId] = url;

                    imageIndex += 1;
                });
            }

            // Echoed parameters.

            if (this.options.lineItemId) {
                configuration.lineItemId = this.options.lineItemId;
            }

            if (this.options.instance) {
                configuration.originalInstanceId = this.options.instance;
            }

            if (this.options.originalMasterId) {
                configuration.originalMasterId = this.options.originalMasterId;
            }

            if (this.viewController?.blueprint?.custom?.['fj-master-product']) {
                configuration.pdpMasterProduct = this.viewController?.blueprint?.custom?.['fj-master-product'];
            }

            // Recipe ID for tracking.

            configuration.driveRecipe = recipe.id;

            return configuration;
        },

        /**
         * Sends WebTop data to whoever needs it.
         */
        useInstanceService(serviceType, serviceUrl, webTopData) {
            if (serviceType == null || serviceUrl == null) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                // Use JSONP.
                if (serviceType === 'JSONP') {
                    const url = new URL(serviceUrl);

                    Object.entries(webTopData).forEach(([k, v]) => {
                        url.searchParams.append(k, v);
                    });

                    // Whelp, services always use `angular.callbacks._0` callback.

                    window.angular = {
                        callbacks: {
                            _0: (response) => {
                                // eslint-disable-next-line
                                console.log('Service response:', response);

                                resolve(response);
                            },
                        },
                    };

                    const script = document.createElement('script');
                    script.src = url.toString();
                    document.head.appendChild(script);
                } else {
                    // Otherwise fallback to regular POST.
                    fetch(serviceUrl, {
                        method: 'post',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(webTopData),
                    })
                        .then((response) => response.json())
                        .then((data) => {
                            resolve(data);
                        });
                }
            });
        },

        /**
         * Add to cart, notify the host, and exit.
         */
        addToCartAndExit() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance().then((recipe) => {
                const webTopData = this.formatDataFromRecipe(recipe);

                let standardFlow = true;

                const continueSave = () => {
                    if (standardFlow) {
                        // Follows the standard flow using the service configured on the vendor object.
                        let serviceType = 'POST';

                        if (this.vendor?.cartServiceType) {
                            serviceType = this.vendor.cartServiceType;
                        }

                        if (this.options.cartServiceType) {
                            serviceType = this.options.cartServiceType;
                        }

                        let serviceUrl = null;

                        if (this.vendor?.cartUrl) {
                            serviceUrl = this.vendor.cartUrl;
                        }

                        if (this.options.cartUrl) {
                            serviceUrl = this.options.cartUrl;
                        }

                        this.useInstanceService(serviceType, serviceUrl, webTopData).then((response) => {
                            let canClose = true;

                            if (this.options.onAddedToCart) {
                                let looksLikeJson = response;

                                try {
                                    looksLikeJson = JSON.parse(looksLikeJson);
                                } catch (e) {
                                    // Nothing.
                                }

                                if (this.options.onAddedToCart(webTopData, looksLikeJson)) {
                                    canClose = false;
                                }
                            }

                            if (canClose) {
                                this.globalDone();
                            }
                        });
                    } else {
                        let canClose = true;

                        if (this.options.onAddedToCart) {
                            if (this.options.onAddedToCart(webTopData)) {
                                canClose = false;
                            }
                        }

                        if (canClose) {
                            this.globalDone();
                        }
                    }
                };

                if (this.options.onAddToCart) {
                    const result = this.options.onAddToCart(webTopData);

                    if (result instanceof Promise) {
                        result.then((resolved) => {
                            if (resolved) {
                                standardFlow = false;
                            }

                            continueSave();
                        });
                    } else {
                        if (result) {
                            standardFlow = false;
                        }

                        continueSave();
                    }
                } else {
                    continueSave();
                }
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        /**
         * Save an instance, notify the host to save design, and exit.
         */
        saveAndExit() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance().then((recipe) => {
                const webTopData = this.formatDataFromRecipe(recipe);

                const continueSave = () => {
                    // There is no standard flow for save design, the host should provide onSaveDesign callback.
                    let canClose = true;

                    if (this.options.onSavedDesign) {
                        if (this.options.onSavedDesign(webTopData)) {
                            canClose = false;
                        }
                    }

                    if (canClose) {
                        this.globalDone();
                    }
                };

                if (this.options.onSaveDesign) {
                    const result = this.options.onSaveDesign(webTopData);

                    if (result instanceof Promise) {
                        result.then(() => {
                            continueSave();
                        });
                    } else {
                        continueSave();
                    }
                } else {
                    continueSave();
                }
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        /**
         * Save an instance and copy the generated link to clipboard.
         */
        saveAndCopyLink(options) {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance({
                instanceOnly: true,
            }).then((instance) => {
                const continueSave = () => {
                    let url;

                    if (window.location.href.includes('demandware.store')) {
                        url = `${this.buildBaseUrl()}/MyJoys-Customize?customInstanceId=${instance.instance}`;
                    } else {
                        url = `${this.buildBaseUrl()}/myjoys-customize?customInstanceId=${instance.instance}`;
                    }

                    const closeCopy = (success) => {
                        appStore.setIsBusy(false);

                        if (success) {
                            appStore.setLinkCopied(true);
                        }
                    };

                    // Try to use browser's share
                    const useShare = () => {
                        if (navigator.share) {
                            if (options && options.image) {
                                fetch(options.image).then((r) => r.blob()).then((blob) => {
                                    const file = new File([blob], `${this.viewController.blueprint.name}.png`, { type: blob.type });

                                    const item = {
                                        title: this.viewController.blueprint.name,
                                        url,
                                        files: [file],
                                    };

                                    try {
                                        navigator.share(item);

                                        closeCopy(true);
                                    } catch (e) {
                                        closeCopy(false);
                                    }
                                });
                            } else {
                                const item = {
                                    title: this.viewController.blueprint.name,
                                    url,
                                };

                                try {
                                    navigator.share(item);

                                    closeCopy(true);
                                } catch (e) {
                                    closeCopy(false);
                                }
                            }
                        } else {
                            closeCopy(false);
                        }
                    };

                    // Try to write into clipboard...
                    const tryCopy = () => {
                        if (navigator.clipboard) {
                            if (navigator.clipboard.write) {
                                const type = 'text/plain';
                                const blob = new Blob([url], { type });
                                // eslint-disable-next-line no-undef
                                const data = [new ClipboardItem({ [type]: blob })];

                                navigator.clipboard.write(data).then(() => {
                                    closeCopy(true);
                                }).catch(() => {
                                    useShare();
                                });
                            } else {
                                try {
                                    navigator.clipboard.writeText(url);
                                    closeCopy(true);
                                } catch (e) {
                                    useShare();
                                }
                            }
                        } else {
                            useShare();
                        }
                    };

                    navigator.permissions.query({ name: 'clipboard-write' }).then(() => {
                        tryCopy();
                    }).catch(() => {
                        tryCopy();
                    });
                };

                continueSave();
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        buildBaseUrl() {
            // Test mode.
            if (window.location.href.includes('demandware.store')) {
                if (window.CQuotient) {
                    // eslint-disable-next-line max-len
                    return `${window.location.protocol}//${window.location.host}/on/demandware.store/Sites-${window.CQuotient.siteId}-Site/${window.CQuotient.locale}`;
                }
            }

            // Does it look like that the site is using /s/ urls with locales?
            if (/^\/s\/footjoy[a-z-]*\/[a-z][a-z]_[A-Z][A-Z]\//.test(window.location.pathname)) {
                const locale = `${window.location.pathname.split('/').slice(0, 4).join('/')}`;

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Does it look like that the site is using SFCC /s/ urls without locales?
            if (/^\/s\/footjoy/.test(window.location.pathname)) {
                const locale = `${window.location.pathname.split('/').slice(0, 3).join('/')}`;

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Does it look like that the site is using locales?
            if (/\/[a-z][a-z]_[A-Z][A-Z]\//.test(window.location.pathname)) {
                const secondSlash = window.location.pathname.indexOf('/', 1);

                const locale = window.location.pathname.substr(0, secondSlash);

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Fallback.
            return `${window.location.protocol}//${window.location.host}`;
        },

        /**
         * Save an instance and open print page in new tab.
         */
        saveAndPrint() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance({
                forceSize: true,
            }).then((recipe) => {
                const webTopData = this.formatDataFromRecipe(recipe);
                const instanceId = webTopData.custom_instance_id;

                const continueSave = () => {
                    appStore.setIsBusy(false);

                    window.open(`https://www.footjoy.com/myjoys/printfriendly/${this.options.vendor}/${instanceId}/?&price=1`, '_blank');
                };

                continueSave();
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        /**
         * Save an instance and sends an email.
         */
        saveAndShare(options) {
            return new Promise((resolve) => {
                const appStore = useAppStore();

                appStore.setIsBusy(true);

                this.saveInstance({
                    forceSize: true,
                }).then((recipe) => {
                    const webTopData = this.formatDataFromRecipe(recipe);
                    const instanceId = webTopData.custom_instance_id;

                    const parameters = {
                        to_email: options.toEmail,
                        to_name: options.to,
                        from_email: options.fromEmail,
                        from_name: options.from,
                        copy_me: options.sendCopy,
                        message: options.message,
                        subject: this.useTranslations('EmailToFriendSubject'),
                        salutation: this.useTranslations('EmailToFriendSalutation'),
                        custom_instance_id: instanceId,
                        sales_office_id: this.vendor.sales_office_id,
                        vendorKey: this.options.vendor ?? '',
                        myJoysImageUrl0: webTopData.myJoysImageUrl0,
                        myJoysImageUrl1: webTopData.myJoysImageUrl1,
                        myJoysImageUrl2: webTopData.myJoysImageUrl2,
                        myJoysImageUrl3: webTopData.myJoysImageUrl3,
                        myJoysImageUrl4: webTopData.myJoysImageUrl4,
                        myJoysImageUrl5: webTopData.myJoysImageUrl5,
                        myJoysImageUrl6: webTopData.myJoysImageUrl6,
                    };

                    // Save WebTop instance.
                    fetch(getServiceUrl('send-to-friend', this.options), {
                        method: 'post',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            vendor: this.options.vendor,
                            environment: this.options.environment,
                            parameters,
                        }),
                    })
                        .then(() => {
                            appStore.setIsBusy(false);

                            resolve();
                        });
                }).catch(() => {
                    appStore.setIsBusy(false);
                });
            });
        },

        /**
         * Save an instance, notify the host to add to wishlist, and exit.
         */
        addToWishlistAndExit() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance().then((recipe) => {
                const webTopData = this.formatDataFromRecipe(recipe);

                let standardFlow = true;

                const continueSave = () => {
                    if (standardFlow) {
                        // Follows the standard flow using the service configured on the vendor object.
                        let serviceType = 'POST';

                        if (this.vendor?.cartServiceType) {
                            serviceType = this.vendor.cartServiceType;
                        }

                        if (this.options.cartServiceType) {
                            serviceType = this.options.cartServiceType;
                        }

                        let serviceUrl = null;

                        if (this.vendor?.wishlistUrl) {
                            serviceUrl = this.vendor.wishlistUrl;
                        }

                        if (this.options.wishlistUrl) {
                            serviceUrl = this.options.wishlistUrl;
                        }

                        this.useInstanceService(serviceType, serviceUrl, webTopData).then((response) => {
                            let canClose = true;

                            if (this.options.onAddedToWishlist) {
                                let looksLikeJson = response;

                                try {
                                    looksLikeJson = JSON.parse(looksLikeJson);
                                } catch (e) {
                                    // Nothing.
                                }

                                if (this.options.onAddedToWishlist(webTopData, looksLikeJson)) {
                                    canClose = false;
                                    appStore.setIsBusy(false);
                                    // If user is not logged in, set message so modal is displayed
                                    if (!looksLikeJson.forwardUser) {
                                        this.setAddToWishlistMessage(looksLikeJson);
                                    } else {
                                        // User is already logged in, take them to their wishlist
                                        this.setAddToWishlistMessage(null);
                                        window.open(looksLikeJson.url, '_self');
                                    }
                                }
                            }

                            if (canClose) {
                                this.globalDone();
                            }
                        });
                    } else {
                        let canClose = true;

                        if (this.options.onAddedToWishlist) {
                            if (this.options.onAddedToWishlist(webTopData)) {
                                canClose = false;
                            }
                        }

                        if (canClose) {
                            this.globalDone();
                        }
                    }
                };

                if (this.options.onAddToWishlist) {
                    const result = this.options.onAddToWishlist(webTopData);

                    if (result instanceof Promise) {
                        result.then((resolved) => {
                            if (resolved) {
                                standardFlow = false;
                            }

                            continueSave();
                        });
                    } else {
                        if (result) {
                            standardFlow = false;
                        }

                        continueSave();
                    }
                } else {
                    continueSave();
                }
            });
        },

        /**
         * The widget has done whatever it was doing and now it is time to close. Should we destroy the instance or just keep it
         * (for example if the host page redirects anyway).
         */
        globalDone() {
            const appStore = useAppStore();

            appStore.globalDone();
        },

        /**
         * Checks if customer prefers a different sizes from the start.
         */
        preferDifferentSizes() {
            return this.options.preferDifferentSizes;
        },

        /**
         * Checks if component is backordered and returns the availability date.
         */
        isBackordered(component) {
            if (this.backorderLookup[component.code]) {
                return this.backorderLookup[component.code];
            }

            return false;
        },

        /**
         * Sets instance error flag.
         */
        setInstanceError(isError) {
            this.instanceError = isError;
        },

        /**
         * Clear personalization from a placement.
         */
        clearPlacement(controller, placementCode) {
            const selectedComponent = controller.selectedAtPlacement(placementCode);
            const selectedPlacement = controller.findPlacement(placementCode);

            if (selectedComponent.custom['dynamic-image-url'] && selectedComponent.custom['dynamic-image-url'].length > 0) {
                controller.updateDynamicImage(placementCode, {
                    title: '',
                    name: '',
                    url: '',
                    pngUrl: '',
                    jpgUrl: '',
                    width: '',
                    height: '',
                });

                controller.commitCustomAttributes(placementCode, {
                    'fj-parameter-value': '',
                }, {
                    propagate: true,
                });
            } else if (selectedComponent.custom['personalization-text'] && selectedComponent.custom['personalization-text'].length > 0) {
                controller.updatePersonalization(placementCode, '');
            } else {
                const placeholder = selectedPlacement.components.find((x) => x.placeholder);

                if (placeholder) {
                    controller.updateComponent(placementCode, placeholder.code);
                }
            }

            controller.commitCustomAttributes(placementCode, {
                'personalization-filled': '',
            }, {
                propagate: true,
            });

            controller.clearComponent(placementCode);
        },

        isPersonalizationPlacement(placement) {
            return placement.code.startsWith('MONOGRAMMINGLOGO')
                || placement.code.startsWith('MONOGRAMMINGIMAGE')
                || placement.code.startsWith('MONOGRAMMINGTEXT');
        },

        isPersonalizationGroup(group) {
            return group.placements.some((placement) => this.isPersonalizationPlacement(placement));
        },

        isBlankPersonalization(group) {
            return group.placements.every((placement) => {
                if (this.isPersonalizationPlacement(placement)) {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    if (!selected?.custom['personalization-filled']) {
                        return true;
                    }
                }

                return false;
            });
        },

        selectedPersonalizationType(group) {
            const imagePlacement = group.placements.find((x) => x.code.startsWith('MONOGRAMMINGIMAGE'));

            if (imagePlacement) {
                if (ComponentUtilities.isFilled(this.viewController.selectedAtPlacement(imagePlacement.code))) {
                    return 'personalization-logo';
                }
            }

            const logoPlacement = group.placements.find((x) => x.code.startsWith('MONOGRAMMINGLOGO'));

            if (logoPlacement) {
                if (ComponentUtilities.isFilled(this.viewController.selectedAtPlacement(logoPlacement.code))) {
                    return 'personalization-logo';
                }
            }

            const textPlacement = group.placements.find((x) => x.code.startsWith('MONOGRAMMINGTEXT'));

            if (textPlacement) {
                if (ComponentUtilities.isFilled(this.viewController.selectedAtPlacement(textPlacement.code))) {
                    return 'personalization-monogram';
                }
            }

            return 'personalization-none';
        },

        selectedPersonalization(group) {
            const filledPlacement = group.placements.find((placement) => {
                if (this.isPersonalizationPlacement(placement)) {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    return ComponentUtilities.isFilled(selected);
                }

                return null;
            });

            if (filledPlacement) {
                const filled = this.viewController.selectedAtPlacement(filledPlacement.code);

                return filled;
            }

            const filledishPlacement = group.placements.find((placement) => {
                if (this.isPersonalizationPlacement(placement)) {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    return ComponentUtilities.isFilledish(selected);
                }

                return null;
            });

            if (filledishPlacement) {
                const filled = this.viewController.selectedAtPlacement(filledishPlacement.code);

                return filled;
            }

            return null;
        },

        clearPersonalization(group) {
            if (group.placements) {
                group.placements.forEach((placement) => {
                    if (this.isPersonalizationPlacement(placement)) {
                        try {
                            this.clearPlacement(this.viewController, placement.code);
                        } catch (e) {
                            // eslint-disable-next-line
                            console.log(e);
                        }
                    }
                });
            }
        },

        markPersonalized(placement) {
            if (placement) {
                this.viewController.commitCustomAttributes(placement.code, {
                    'personalization-filled': 'true',
                }, {
                    propagate: true,
                });
            }
        },

        setMirrorPersonalization(v) {
            this.mirrorPersonalization = v;
        },

        mirrorToAnotherSide(group) {
            // Mirror to another side.
            let otherGroup;

            if (group.code.includes('Left')) {
                otherGroup = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationRight');
            } else {
                otherGroup = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationLeft');
            }

            this.clearPersonalization(otherGroup);

            const selectedType = this.selectedPersonalizationType(group);

            // Find a similar placement on the other side.
            if (selectedType === 'personalization-logo') {
                let thisSide;
                let otherSide;

                if (group.code.includes('Left')) {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGLOGO21');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGLOGO11');
                } else {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGLOGO11');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGLOGO21');
                }

                // Is it actually a logo?
                let thisComponent = this.viewController.selectedAtPlacement(thisSide.code);

                if (thisComponent && !thisComponent.placeholder) {
                    const otherComponent = otherSide.components.find((x) => x.placementIndependentCode === thisComponent.placementIndependentCode);

                    if (otherComponent) {
                        this.viewController.updateComponent(otherSide.code, otherComponent.code);
                    }
                } else {
                    // It is probably a vendor logo.
                    if (group.code.includes('Left')) {
                        thisSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE21');
                        otherSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE11');
                    } else {
                        thisSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE11');
                        otherSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE21');
                    }

                    thisComponent = this.viewController.selectedAtPlacement(thisSide.code);

                    const url = thisComponent?.custom?.['dynamic-image-url'];

                    if (url?.length > 0) {
                        // Yup. Apply to another side.
                        const attributes = {
                            'dynamic-image-name': '',
                            'dynamic-image-offset-x': '',
                            'dynamic-image-offset-y': '',
                            'dynamic-image-rotate': '',
                            'dynamic-image-scale': '',
                            'dynamic-image-title': '',
                            'dynamic-image-width': '',
                            'dynamic-image-height': '',
                            'dynamic-image-url': '',
                            'dynamic-image-x': '',
                            'dynamic-image-y': '',
                            'fj-parameter-value': '',
                            'fj-parameter-value-name': '',
                            'personalization-filled': '',
                        };

                        // eslint-disable-next-line no-restricted-syntax
                        for (const k of Object.keys(attributes)) {
                            attributes[k] = thisComponent.custom[k];
                        }

                        this.viewController.commitCustomAttributes(otherSide.code, attributes);
                    }
                }
            }

            if (selectedType === 'personalization-monogram') {
                let thisSide;
                let otherSide;

                if (group.code.includes('Left')) {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGTEXT21');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGTEXT11');
                } else {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGTEXT11');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGTEXT21');
                }

                const thisComponent = this.viewController.selectedAtPlacement(thisSide.code);

                if (thisComponent && !thisComponent.placeholder) {
                    const otherComponent = otherSide.components.find((x) => x.placementIndependentCode === thisComponent.placementIndependentCode);

                    if (otherComponent) {
                        this.viewController.updateComponent(otherSide.code, otherComponent.code);

                        const text = thisComponent.custom['personalization-text'];

                        if (text) {
                            this.viewController.updatePersonalization(otherSide.code, text);
                        }
                    }
                }
            }
        },

        make360Video(duration) {
            emitter.$emit('make360Video', duration);
        },

        populateGallerySpecialMessage() {
            if (this.specialMessage == null) {
                this.specialMessage = {};
            }

            const icon = this.translateWithGallery('SpecialMessage-icon');
            if (icon?.length > 0) {
                this.specialMessage.icon = icon;
            }

            const title = this.translateWithGallery('SpecialMessage-title');
            if (title?.length > 0) {
                this.specialMessage.title = title;
            }

            const body = this.translateWithGallery('SpecialMessage-body');
            if (body?.length > 0) {
                this.specialMessage.body = body;
            }
        },

        populatePersonalizationPolicy() {
            const body = this.translateWithGallery('Policies-Personalization');
            if (body?.length > 0) {
                this.personalizationPolicy = body;
            }
        },

        populateGalleryTranslations() {
            this.populateGallerySpecialMessage();
            this.populatePersonalizationPolicy();
        },

        /**
         * Find a translation for a single option.
         */
        translateGalleryOption(placement, key) {
            const c = placement.components.find((x) => x.placementIndependentCode.endsWith(key));

            return c;
        },

        /**
         * Tries to translate the key using the text gallery first.
         */
        translateWithGallery(key) {
            if (this.textGallery == null) {
                this.viewController?.blueprint?.groups.forEach((g) => {
                    g.placements?.forEach((p) => {
                        if (p.custom?.['fj-gallery'] === 'text') {
                            this.textGallery = p;
                        }
                    });
                });
            }

            if (this.textGallery) {
                let option = this.translateGalleryOption(this.textGallery, `-${this.viewController.blueprint.styleCode}${key}`);
                if (option == null) {
                    option = this.translateGalleryOption(this.textGallery, `-${key}`);
                }
                if (option) {
                    if (option?.description?.[0]?.description) {
                        return option?.description?.[0]?.description;
                    }
                }
            }

            return null;
        },
    },
});
